import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Search, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { GradButton } from "@/components/erp";
import { UrunGorsel } from "@/components/UrunGorsel";
import api, { errMsg } from "@/lib/api";

const sade = (s) => String(s || "").toLocaleLowerCase("tr").replace(/\s+/g, "");
const parcala = (q) => String(q || "").toLocaleLowerCase("tr").split(/\s+/).filter(Boolean);
const GOSTER = 300;

const Secenek = ({ id, checked, onChange, children }) => (
  <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none"><Checkbox data-testid={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} />{children}</label>
);

const ResimSec = ({ dosya, setDosya }) => (
  <label data-testid="toplu-gorsel-sec" className="flex items-center gap-3 p-3 rounded-xl border border-dashed border-white/20 hover:bg-white/5 cursor-pointer transition-colors">
    {dosya ? <img src={URL.createObjectURL(dosya)} alt="" className="w-16 h-16 rounded-lg object-cover bg-white" data-testid="toplu-gorsel-onizleme" />
      : <span className="w-16 h-16 rounded-lg bg-white/5 flex items-center justify-center"><ImagePlus className="w-6 h-6 text-muted-foreground" /></span>}
    <span className="text-sm">{dosya ? dosya.name : "Uygulanacak resmi seçin (PNG / JPEG / WEBP, en fazla 5 MB)"}</span>
    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="toplu-gorsel-dosya" onChange={(e) => setDosya(e.target.files?.[0] || null)} />
  </label>
);

export const TopluGorsel = ({ urunler, onClose, onKaydedildi }) => {
  const [q, setQ] = useState("");
  const [resimsiz, setResimsiz] = useState(true);
  const [mamul, setMamul] = useState(true);
  const [secili, setSecili] = useState(new Set());
  const [dosya, setDosya] = useState(null);
  const [busy, setBusy] = useState(false);

  const liste = useMemo(() => {
    const p = parcala(q);
    return urunler.filter((u) => {
      if ((resimsiz && u.gorsel_yolu) || (mamul && u.kategori_tipi !== "mamul")) return false;
      const metin = [u.kod, u.ad, u.ozellikler?.olcu].map(sade).join(" ");
      return p.every((k) => metin.includes(k));
    });
  }, [urunler, q, resimsiz, mamul]);
  const hepsi = liste.length > 0 && liste.every((u) => secili.has(u.id));
  const degis = (id, v) => setSecili((s) => { const n = new Set(s); v ? n.add(id) : n.delete(id); return n; });
  const tumu = (v) => setSecili((s) => { const n = new Set(s); liste.forEach((u) => (v ? n.add(u.id) : n.delete(u.id))); return n; });

  const uygula = async () => {
    if (!dosya) { toast.error("Önce bir resim seçin"); return; }
    if (!secili.size) { toast.error("En az bir ürün seçin"); return; }
    const fd = new FormData(); fd.append("dosya", dosya); fd.append("urun_idler", [...secili].join(","));
    setBusy(true);
    try {
      const { data } = await api.post("/urun-gorsel-toplu", fd);
      toast.success(`${data.guncellenen} ürüne resim uygulandı`);
      setSecili(new Set()); setDosya(null); await onKaydedildi();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col" data-testid="toplu-gorsel-dialog">
        <DialogHeader>
          <DialogTitle>Toplu resim yükle</DialogTitle>
          <DialogDescription>Ölçüye göre süzün (ör. <span className="font-mono">x200x200</span> veya <span className="font-mono">x1000</span>), ürünleri seçin ve tek resmi hepsine uygulayın.</DialogDescription>
        </DialogHeader>
        <ResimSec dosya={dosya} setDosya={setDosya} />
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus data-testid="toplu-gorsel-ara" placeholder="Kod, ad veya ölçü içinde ara… (ör. x200x200 veya BMW x200x200)" value={q} onChange={(e) => { setQ(e.target.value); setSecili(new Set()); }} className="pl-9 bg-white/5" />
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <Secenek id="toplu-gorsel-tumu" checked={hepsi} onChange={tumu}>Listedekilerin tümünü seç ({liste.length})</Secenek>
          <Secenek id="toplu-gorsel-resimsiz" checked={resimsiz} onChange={setResimsiz}>Sadece resimsizler</Secenek>
          <Secenek id="toplu-gorsel-mamul" checked={mamul} onChange={setMamul}>Sadece mamuller</Secenek>
        </div>
        <div className="flex-1 overflow-y-auto min-h-[200px] rounded-xl border border-white/10 divide-y divide-white/5" data-testid="toplu-gorsel-liste">
          {liste.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground" data-testid="toplu-gorsel-bos">Eşleşen ürün yok.</div>}
          {liste.slice(0, GOSTER).map((u) => (
            <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-white/[0.03] cursor-pointer" data-testid={`toplu-gorsel-satir-${u.kod}`}>
              <Checkbox data-testid={`toplu-gorsel-sec-${u.kod}`} checked={secili.has(u.id)} onCheckedChange={(v) => degis(u.id, !!v)} />
              <UrunGorsel id={u.id} var={!!u.gorsel_yolu} className="w-9 h-9" />
              <span className="font-mono text-xs w-32 shrink-0 truncate">{u.kod}</span>
              <span className="text-sm truncate">{u.ad}</span>
            </label>
          ))}
          {liste.length > GOSTER && <div className="p-3 text-center text-xs text-muted-foreground">İlk {GOSTER} ürün gösteriliyor; "tümünü seç" listedeki {liste.length} ürünün hepsini seçer.</div>}
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-muted-foreground" data-testid="toplu-gorsel-secili">{secili.size} ürün seçili</span>
          <GradButton data-testid="toplu-gorsel-uygula" onClick={uygula} disabled={busy}><Upload className="w-4 h-4" />{busy ? "Yükleniyor…" : "Seçili ürünlere uygula"}</GradButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};
